/**
 * Series-slot assignments for the dashboard charts (design-port ALL-5825).
 *
 * A slot is a chart colour (`var(--ads-chart-N)`), so an entity keeps the same
 * colour wherever it is drawn: the wallet-type rows in `AumComposition` and the
 * wallet-type nodes in `AumFlow` share `TYPE_SLOT`, and so on. Keys are the
 * labels the charts actually render (`chainShortLabel` for chains, the
 * capitalised wallet type for types), which is why the lookup in every caller
 * carries the prototype's `?? <fallback>` — a chain or provider the design did
 * not enumerate still gets a stable colour instead of `undefined`.
 */
import type { SeriesSlot } from "@/components/data-display/viz";

export const TYPE_SLOT: Record<string, SeriesSlot> = {
  Cold: 3,
  Hot: 1,
  Warm: 4,
  Sandbox: 5,
  Pending: 5,
};

/**
 * Fill colour for a wallet type in the AUM composition bar. The type mix is
 * read as a per-type *share*, so each type owns a data colour token rather than
 * a chart slot (`TYPE_SLOT`, which keeps an entity the same colour across the
 * dashboard's series charts). The three real `WalletType`s are enumerated; the
 * fallback is what the design reserves for anything else, so an unenumerated
 * type is still painted rather than left transparent and read as "no data".
 */
export const TYPE_FILL: Record<string, string> = {
  Hot: "var(--ads-info)",
  Warm: "var(--ads-viz-amber)",
  Cold: "var(--ads-viz-green)",
};

export const TYPE_FILL_FALLBACK = "var(--ads-viz-slate)";

export const CHAIN_SLOT: Record<string, SeriesSlot> = {
  Ethereum: 7,
  Bitcoin: 2,
  Polygon: 5,
  Solana: 6,
  Sepolia: 5,
  Others: 8,
  // Live gateway chain spellings that the prototype's fixture set did not
  // carry; mapped to the same slots as their mainnet counterparts.
  "Ethereum Sepolia": 5,
  "Bitcoin Testnet": 2,
};

export const PROVIDER_SLOT: Record<string, SeriesSlot> = {
  Fireblocks: 1,
  "Mesh (VaultCore)": 8,
  "Alloy-managed signing": 8,
  "Phone co-signed (2-of-3)": 8,
  BitGo: 6,
  "Coinbase Prime": 2,
  Circle: 4,
};

/**
 * Short chain label used as a chart key. Mirrors the live dashboard convention
 * (`chain-distribution.tsx`), so the same chain reads the same way across the
 * dashboard: mainnet spellings drop the " Mainnet" suffix and Ethereum's
 * testnet is shown as "Sepolia".
 */
export function chainShortLabel(chain: string): string {
  if (chain === "Ethereum Sepolia") return "Sepolia";
  return chain.replace(/ Mainnet$/, "");
}
